import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { COLORS } from "@tCentro/theme/design";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    mobileTopGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    mobileTopGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    middleColumnGridItem?: GridItemProps;
    middleColumnGridItemHidden?: HiddenProps;
    zone?: HiddenProps;
    mobileGridItem?: GridItemProps;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            overflow: hidden;
            direction: ltr;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 2;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    middleGridItem: {
        width: [12, 12, 12, 0, 0],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 3;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    middleGridItemHidden: {
        above: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    mobileTopGridItemHidden: {
        above: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    rightColumnGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css``,
                        css`
                            padding-left: 0px;
                            order: 3;
                            // div {
                            //     display: flex;
                            //     align-items: center;
                            //     justify-content: end;
                            // }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    topGridItem: {
        width: 12,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    mobileTopGridItem: {
        width: 12,
    },

    bottomGridItem: {
        width: 12,
        css: css`
            padding: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            order: 2;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    middleColumnGridItem: {
        width: [0, 0, 0, 7, 7],
        css: css`
            margin-top: 20px !important;
        `,
    },
    middleColumnGridItemHidden: {
        below: "lg",
        css: css`
            width: 100%;
            direction: ltr;
            position: relative;
            padding-right: 16px;
        `,
    },
    container: {
        css: css`
            direction: ltr;
            background: ${COLORS.background.grayShade};
            margin-bottom: 24px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 0px !important;
                        `,
                        css`
                            padding: 2px 16px 35px 16px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}

            @media(min-width:1813px) {
                margin-bottom: 0px !important;
            }
        `,
    },
    zone: {
        above: "md",
        css: css`
            display: flex;
            justify-content: end;
            > div {
                display: flex;
                justify-content: end;
                align-items: center;
            }
        `,
    },
    mobileGridItem: {
        width: 12,
        css: css`
            justify-content: end;
        `,
    },
};

const styles = viewStyles;

const ProductDetailsView: React.FC<WidgetProps> = props => {
    const [screenWidth, setScreenWidth] = React.useState(1000);
    React.useEffect(() => {
        const handleResize = () => {
            if (typeof window !== undefined) {
                setScreenWidth(window?.innerWidth);
            }
        };

        window?.addEventListener("resize", handleResize);

        return () => {
            window?.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.leftColumnGridItem} data-test-selector="productDetails_leftColumn">
                <GridContainer {...styles.leftColumnInnerContainer}>
                    <GridItem {...styles.mobileGridItem}>
                        <Hidden {...styles.zone}>
                            <Zone contentId={props.id} zoneName="Content6" />
                        </Hidden>
                    </GridItem>
                    {screenWidth <= 992 && (
                        <GridItem {...styles.mobileTopGridItem}>
                            <Hidden {...styles.mobileTopGridItemHidden}>
                                <Zone contentId={props.id} zoneName="Content5" />
                            </Hidden>
                        </GridItem>
                    )}

                    <GridItem {...styles.topGridItem}>
                        <Zone contentId={props.id} zoneName="Content0" />
                    </GridItem>
                    <GridItem {...styles.middleGridItem}>
                        <Hidden {...styles.middleGridItemHidden}>
                            <Zone contentId={props.id} zoneName="Content1" />
                        </Hidden>
                    </GridItem>
                    <GridItem {...styles.bottomGridItem}>
                        <Zone contentId={props.id} zoneName="Content2" />
                    </GridItem>
                </GridContainer>
            </GridItem>

            {screenWidth >= 992 && (
                <GridItem {...styles.middleColumnGridItem} data-test-selector="productDetails_middleColumn">
                    <Hidden {...styles.middleColumnGridItemHidden}>
                        <Zone contentId={props.id} zoneName="Content3" />
                    </Hidden>
                </GridItem>
            )}

            <GridItem {...styles.rightColumnGridItem} data-test-selector="productDetails_rightColumn">
                <Zone contentId={props.id} zoneName="Content7" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
